import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { contactSchema } from "@/lib/leads/validation";
import { buildLeadTags } from "@/lib/leads/taxonomy";

export type SelfTestCheck = {
  id: string;
  group: "Ambiente" | "Validação" | "Tags & roteamento" | "Auditoria";
  label: string;
  status: "pass" | "fail" | "warn";
  detail: string;
};

// Mesma fonte de verdade do painel: admin_members (+ páginas liberadas).
async function adminGuard(context: { userId: string }) {
  const { assertAdmin } = await import("@/lib/admin/auth.server");
  await assertAdmin(context.userId, "/admin/painel");
}

export const runLeadsSelfTest = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await adminGuard(context as any);
    const checks: SelfTestCheck[] = [];

    // 1. Ambiente
    const envs: Array<[string, string]> = [
      ["RDSTATION_CRM_TOKEN", "RD Station CRM API token"],
      ["RDSTATION_MKT_CLIENT_ID", "RD Station Marketing client_id"],
      ["RDSTATION_MKT_CLIENT_SECRET", "RD Station Marketing client_secret"],
      ["RDSTATION_MKT_REFRESH_TOKEN", "RD Station Marketing refresh_token"],
      ["META_CAPI_TOKEN", "Meta Conversions API access token"],
      ["META_PIXEL_ID", "Meta Pixel ID"],
    ];
    for (const [key, label] of envs) {
      const present = !!process.env[key];
      checks.push({
        id: `env-${key}`,
        group: "Ambiente",
        label,
        status: present ? "pass" : key === "META_PIXEL_ID" ? "warn" : "fail",
        detail: present ? "Secret configurado" : "Secret ausente",
      });
    }

    // 2. Validação
    const valid = contactSchema.safeParse({
      nome: "Lead Teste",
      email: "teste@exemplo.com",
      telefone: "(11) 98888-7777",
    });
    checks.push({
      id: "val-ok",
      group: "Validação",
      label: "Schema aceita lead completo",
      status: valid.success ? "pass" : "fail",
      detail: valid.success ? "OK" : JSON.stringify(valid.error.flatten()),
    });
    const invalid = contactSchema.safeParse({ nome: "A", email: "x", telefone: "1" });
    checks.push({
      id: "val-reject",
      group: "Validação",
      label: "Schema bloqueia lead incompleto",
      status: !invalid.success ? "pass" : "fail",
      detail: !invalid.success ? "Rejeição correta de nome/e-mail/telefone inválidos" : "Aceitou indevidamente",
    });

    // 3. Tags & roteamento
    const scenarios = [
      {
        id: "tag-aula-quente",
        label: "Aula experimental Orto · quente",
        actual: buildLeadTags({ source: "aula-experimental", slug: "orto", classificacao: "quente" }),
        expected: ["aulas-exp-orto", "aulas-exp-todas", "lead-quente"],
      },
      {
        id: "tag-ementa-frio",
        label: "Ementa Dor · frio",
        actual: buildLeadTags({ source: "ementa", slug: "dor", classificacao: "frio" }),
        expected: ["ementa-site-dor", "ementa-site-todas", "lead-frio"],
      },
      {
        id: "tag-precheckout",
        label: "Pré-checkout sempre morno",
        actual: buildLeadTags({ source: "pre-checkout", slug: "esporte", classificacao: "frio" }),
        expected: ["pre-checkout-esporte", "pre-checkout-todas", "lead-morno"],
      },
      {
        id: "tag-precheckout-gg",
        label: "Pré-checkout GG (Google Ads) sempre morno",
        actual: buildLeadTags({ source: "pre-checkout", slug: "orto", variant: "gg" }),
        expected: ["pre-checkout-gg-orto", "pre-checkout-gg-todas", "lead-morno"],
      },

      {
        id: "tag-quizz-parcial",
        label: "Quizz Dor parcial",
        actual: buildLeadTags({ source: "quizz-dor", partial: true }),
        expected: ["quizz-dor", "lead-parcial"],
      },
    ];
    for (const s of scenarios) {
      const ok = s.expected.every((t) => s.actual.includes(t));
      checks.push({
        id: s.id,
        group: "Tags & roteamento",
        label: s.label,
        status: ok ? "pass" : "fail",
        detail: `Tags: ${s.actual.join(", ")}`,
      });
    }

    // 4. Auditoria — confirma tabela acessível e tem registros recentes
    try {
      const since = new Date(Date.now() - 1000 * 60 * 60 * 24 * 7).toISOString();
      const { count, error } = await (context as any).supabase
        .from("lead_capture_attempts")
        .select("id", { count: "exact", head: true })
        .gte("created_at", since);
      if (error) throw error;
      checks.push({
        id: "audit-table",
        group: "Auditoria",
        label: "Tabela lead_capture_attempts acessível",
        status: "pass",
        detail: `${count ?? 0} tentativas nos últimos 7 dias`,
      });
    } catch (e: any) {
      checks.push({
        id: "audit-table",
        group: "Auditoria",
        label: "Tabela lead_capture_attempts acessível",
        status: "fail",
        detail: e?.message ?? "Erro ao consultar",
      });
    }

    const summary = {
      pass: checks.filter((c) => c.status === "pass").length,
      warn: checks.filter((c) => c.status === "warn").length,
      fail: checks.filter((c) => c.status === "fail").length,
    };
    return { checks, summary, ranAt: new Date().toISOString() };
  });
